import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { X } from 'lucide-react';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import { ui } from 'theme/discover';

/**
 * A folha de filtros do app (FiltersSheet): título e "Limpar" no topo, seções
 * de chips no meio e um único botão em gradiente no rodapé. No celular sobe de
 * baixo como no app; no desktop vira um cartão centralizado.
 */
export default function FilterSheet({
    isOpen,
    onClose,
    canClear = false,
    onClear,
    applyLabel = 'Ver resultados',
    onApply,
    children,
}) {
    useEffect(() => {
        if (!isOpen) return undefined;

        function onKeyDown(e) {
            if (e.key === 'Escape') onClose();
        }

        // Sem travar a rolagem, a página de trás rolava junto com a lista de chips.
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', onKeyDown);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <Overlay onClick={onClose}>
            <Sheet
                role='dialog'
                aria-modal='true'
                aria-labelledby='filter-sheet-title'
                onClick={(e) => e.stopPropagation()}
            >
                <Grabber />

                <SheetHeader>
                    <SheetTitle id='filter-sheet-title'>Filtros</SheetTitle>

                    <HeaderActions>
                        <ClearLink type='button' onClick={onClear} disabled={!canClear}>
                            Limpar
                        </ClearLink>
                        <CloseButton type='button' onClick={onClose} aria-label='Fechar filtros'>
                            <X size={18} color={ui.ink} />
                        </CloseButton>
                    </HeaderActions>
                </SheetHeader>

                <SheetBody>
                    {children}
                </SheetBody>

                <SheetFooter>
                    <ApplyButton type='button' onClick={onApply}>
                        {applyLabel}
                    </ApplyButton>
                </SheetFooter>
            </Sheet>
        </Overlay>
    );
}

/** Bloco de filtro: título à esquerda e um resumo opcional à direita, sem texto explicativo. */
export function FilterSection({ title, hint, children }) {
    return (
        <Section>
            <SectionHeader>
                <SectionTitle>{title}</SectionTitle>
                {!!hint && <SectionHint>{hint}</SectionHint>}
            </SectionHeader>
            {children}
        </Section>
    );
}

/** Um clique seleciona: as opções ficam todas à vista, sem o select nativo. */
export function FilterChip({ label, icon, active = false, onClick }) {
    return (
        <Chip type='button' $active={active} aria-pressed={active} onClick={onClick}>
            {icon && <ChipIcon>{icon}</ChipIcon>}
            {label}
        </Chip>
    );
}

/** Slider de distância com a parte percorrida da trilha preenchida em roxo. */
export function FilterRange({ value, min, max, onChange }) {
    const filled = ((value - min) / (max - min)) * 100;

    return (
        <Range
            type='range'
            min={min}
            max={max}
            value={value}
            $filled={filled}
            aria-label='Distância em km'
            onChange={(e) => onChange(Number(e.target.value))}
        />
    );
}

export const ChipsWrap = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

export const FilterLoading = styled.p`
    margin: 0;
    font-size: 13px;
    color: ${ui.inkFaint};
`;

const fadeIn = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const slideUp = keyframes`
    from { transform: translateY(24px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: rgba(21, 19, 42, 0.5);
    backdrop-filter: blur(2px);
    animation: ${fadeIn} 0.2s ease;

    @media screen and (min-width: ${breakpoints.lg}) {
        align-items: center;
        padding: 24px;
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

// Os cantos de 26px do sheet do detalhe, como no app.
const Sheet = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    max-height: 88vh;
    padding: 8px 20px 0;
    border-radius: 26px 26px 0 0;
    background: ${colors.neutralLight};
    box-shadow: 0 -8px 40px rgba(21, 19, 42, 0.16);
    animation: ${slideUp} 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 560px;
        max-height: 85vh;
        padding: 20px 28px 0;
        border-radius: 24px;
        box-shadow: 0 24px 64px rgba(21, 19, 42, 0.24);
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`;

const Grabber = styled.div`
    align-self: center;
    width: 40px;
    height: 4px;
    margin-bottom: 12px;
    border-radius: 2px;
    background: ${ui.border};

    @media screen and (min-width: ${breakpoints.lg}) {
        display: none;
    }
`;

const SheetHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 14px;
    border-bottom: 1px solid ${ui.border};
`;

const SheetTitle = styled.h2`
    margin: 0;
    font-size: 22px;
    line-height: 1.2;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: ${ui.ink};
`;

const HeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
`;

// Roxo só quando há o que limpar; sem filtro fica cinza e desabilitado de fato.
const ClearLink = styled.button`
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-size: 14px;
    font-weight: 700;
    color: ${ui.link};
    cursor: pointer;

    &:hover:not(:disabled) {
        text-decoration: underline;
    }

    &:disabled {
        color: ${ui.inkFaint};
        cursor: default;
    }
`;

const CloseButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 50%;
    background: ${ui.surfaceSunken};
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: ${ui.border};
    }

    &:focus-visible {
        outline: 2px solid ${ui.link};
        outline-offset: 2px;
    }
`;

const SheetBody = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-bottom: 20px;
    overscroll-behavior: contain;
`;

const SheetFooter = styled.div`
    padding: 12px 0 calc(16px + env(safe-area-inset-bottom));
    border-top: 1px solid ${ui.border};

    @media screen and (min-width: ${breakpoints.lg}) {
        padding-bottom: 20px;
    }
`;

const ApplyButton = styled.button`
    width: 100%;
    height: 52px;
    border: none;
    border-radius: 999px;
    font: inherit;
    font-size: 15px;
    font-weight: 700;
    color: ${colors.neutralLight};
    background: ${ui.cta};
    box-shadow: 0 8px 20px rgba(105, 12, 228, 0.28);
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    &:hover {
        box-shadow: 0 10px 24px rgba(105, 12, 228, 0.36);
    }

    &:active {
        transform: scale(0.98);
    }

    &:focus-visible {
        outline: 2px solid ${ui.link};
        outline-offset: 3px;
    }
`;

const Section = styled.section`
    margin-top: 24px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: ${ui.ink};
`;

const SectionHint = styled.span`
    font-size: 13px;
    color: ${ui.inkSoft};
`;

// O desenho das pílulas do redesign (.mpill), as mesmas da home.
const Chip = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 15px;
    border-radius: 999px;
    border: 1px solid ${({ $active }) => $active ? ui.link : ui.border};
    font: inherit;
    font-size: 13.5px;
    font-weight: ${({ $active }) => $active ? 700 : 500};
    line-height: 1.2;
    color: ${({ $active }) => $active ? colors.neutralLight : ui.ink};
    background: ${({ $active }) => $active ? ui.link : ui.surfaceMuted};
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    /* Os ícones do lucide seguem o color; os das características recebem a cor por prop. */
    svg {
        color: ${({ $active }) => $active ? colors.neutralLight : ui.link};
    }

    &:hover {
        border-color: ${ui.link};
    }

    &:focus-visible {
        outline: 2px solid ${ui.link};
        outline-offset: 2px;
    }
`;

const ChipIcon = styled.span`
    display: flex;
    width: 14px;
    height: 14px;

    & > svg {
        width: 14px;
        height: 14px;
    }
`;

const Range = styled.input`
    width: 100%;
    height: 6px;
    margin: 6px 0;
    border-radius: 3px;
    appearance: none;
    background: linear-gradient(
        to right,
        ${ui.link} 0%,
        ${ui.link} ${({ $filled }) => $filled}%,
        ${ui.surfaceSunken} ${({ $filled }) => $filled}%,
        ${ui.surfaceSunken} 100%
    );
    cursor: pointer;

    &::-webkit-slider-thumb {
        appearance: none;
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: 3px solid ${colors.neutralLight};
        background: ${ui.link};
        box-shadow: 0 2px 6px rgba(21, 19, 42, 0.25);
    }

    &::-moz-range-thumb {
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 3px solid ${colors.neutralLight};
        background: ${ui.link};
        box-shadow: 0 2px 6px rgba(21, 19, 42, 0.25);
    }

    &:focus-visible {
        outline: 2px solid ${ui.link};
        outline-offset: 6px;
    }
`;
